/**
 * useGlobalTrendyolLive — global Trendyol Canlı Sipariş dinleyicisi
 *
 * Layout.jsx tarafından tetiklenir. Kullanıcı hangi sayfada olursa olsun
 * arka planda çalışır. Yeni sipariş geldiğinde:
 *   • Ses (şube ayarındaki tona göre)
 *   • Toast bildirimi
 *   • Sistem push notification
 *   • Otomatik onay (settings.auto_accept + liveMode='auto')
 *   • Otomatik yazdırma (settings.auto_print — hidden iframe)
 *
 * Etkinleşme kuralları:
 *   • Sadece giriş yapmış kullanıcılar (admin/regional/branch)
 *   • Sadece login sayfası ve public route'lar (/s/*, /screen/*) dışında
 *   • liveMode ve settings LocalStorage'dan okunur (Canlı Sipariş sayfasıyla senkron)
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Ses altyapısı (TrendyolLive.jsx ile aynı — burada da modül-seviyesinde tek instance)
let _audioCtx = null;
let _audioUnlocked = false;
function _getAudioCtx() {
  if (_audioCtx) return _audioCtx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _audioCtx = new AC();
    return _audioCtx;
  } catch { return null; }
}
function _installAudioUnlock() {
  if (_audioUnlocked) return;
  const unlock = async () => {
    try {
      const ctx = _getAudioCtx();
      if (ctx && ctx.state === "suspended") await ctx.resume();
      _audioUnlocked = true;
    } catch { /* noop */ }
  };
  window.addEventListener("click", unlock);
  window.addEventListener("keydown", unlock);
  window.addEventListener("touchstart", unlock);
}
function playBeep(tone = "classic") {
  const ctx = _getAudioCtx();
  if (!ctx) return false;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  try {
    const base = ctx.currentTime + 0.02;
    const play = (t0, freq, dur, type = "square", vol = 0.7) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = type;
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.001, t0);
      gain.gain.exponentialRampToValueAtTime(vol, t0 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
      osc.start(t0); osc.stop(t0 + dur + 0.02);
    };
    if (tone === "alarm") { for (let i = 0; i < 5; i++) play(base + i * 0.22, 1500, 0.18, "square", 0.8); }
    else if (tone === "ding") { play(base, 659, 0.35, "sine", 0.55); play(base + 0.15, 784, 0.5, "sine", 0.55); }
    else if (tone === "buzzer") { for (let i = 0; i < 4; i++) play(base + i * 0.18, 400, 0.14, "sawtooth", 0.7); }
    else if (tone === "bell") { play(base, 1000, 0.5, "triangle", 0.7); play(base + 0.6, 800, 0.5, "triangle", 0.7); }
    else { for (let i = 0; i < 3; i++) play(base + i * 0.32, i % 2 === 0 ? 1200 : 880, 0.28, "square", 0.7); }
    return true;
  } catch { return false; }
}

function showNotification(title, body, tag) {
  try {
    if (!("Notification" in window) || Notification.permission !== "granted") return false;
    const n = new Notification(title, {
      body, tag, requireInteraction: false, silent: false,
      icon: "/logo192.png", badge: "/logo192.png",
    });
    setTimeout(() => { try { n.close(); } catch { /* noop */ } }, 15000);
    n.onclick = () => { try { window.focus(); n.close(); } catch { /* noop */ } };
    return true;
  } catch { return false; }
}

const fmtTL = (v) => `₺${Number(v || 0).toFixed(2)}`;

/**
 * Otomatik yazdırma için hidden iframe.
 * Sayfa herhangi bir yerdeyken çalışır — TrendyolLive'a bağımlı değil.
 */
function printOrderSilent(orderHtml) {
  try {
    const blob = new Blob([orderHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;";
    let printed = false;
    const doPrint = () => {
      if (printed) return;
      printed = true;
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) { console.warn("[global-print] fail:", e); }
      setTimeout(() => {
        try { document.body.removeChild(iframe); } catch { /* noop */ }
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
      }, 10000);
    };
    iframe.onload = () => setTimeout(doPrint, 300);
    document.body.appendChild(iframe);
    iframe.src = url;
    setTimeout(doPrint, 2000); // fallback
    return true;
  } catch (e) { console.warn("[global-print] fail:", e); return false; }
}

export default function useGlobalTrendyolLive(user) {
  const esRef = useRef(null);
  const reconnectRef = useRef(null);
  const seenOrdersRef = useRef(new Set());  // aynı siparişi 2 kez işlemeyelim

  useEffect(() => {
    // Yetkisiz kullanıcı → dinleme
    if (!user?.token) return;
    // Login/public rotalar için de dinleme
    const path = window.location.pathname;
    if (path.startsWith("/login") || path.startsWith("/s/") || path.startsWith("/screen/")) return;

    _installAudioUnlock();

    let stopped = false;
    const backend = BACKEND;

    // Şube ayarlarını LocalStorage'a preload et (kullanıcı hiç Canlı Sipariş sayfasına
    // girmemiş olabilir — o zaman cache boş olur ve auto-accept çalışmaz)
    (async () => {
      try {
        const cached = localStorage.getItem("ty_branch_settings_cache");
        if (!cached || cached === "{}") {
          const r = await api.get("/trendyol/branch-settings");
          const rows = r.data || [];
          const map = {};
          rows.forEach((x) => { map[`${x.brand}::${x.branch_id}`] = x; });
          localStorage.setItem("ty_branch_settings_cache", JSON.stringify(map));
        }
      } catch (e) { console.warn("[global-live] settings preload:", e); }
    })();

    const connect = () => {
      if (stopped) return;
      // TrendyolLive sayfası kendi listener'ını çalıştırıyor — çakışmayalım
      if (window.__tyLivePageActive === true) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = setTimeout(connect, 3000);  // sayfa kapanınca devral
        return;
      }
      try {
        const url = `${backend}/api/trendyol/live/stream?token=${encodeURIComponent(user.token)}`;
        const es = new EventSource(url);
        esRef.current = es;

        es.addEventListener("new_order", async (ev) => {
          try {
            const data = JSON.parse(ev.data);
            const pkgId = data.package_id;
            // Dedup — aynı sipariş için 2 kez ses/print/toast çıkmasın
            if (seenOrdersRef.current.has(pkgId)) return;
            seenOrdersRef.current.add(pkgId);
            // 10 dk sonra dedup set'ten sil (memory bounded)
            setTimeout(() => seenOrdersRef.current.delete(pkgId), 10 * 60 * 1000);

            // Ayarları LocalStorage'dan oku (TrendyolLive.jsx senkron kaydediyor)
            let settings = {};
            let liveMode = "monitor";
            let soundOn = true;
            try {
              settings = JSON.parse(localStorage.getItem("ty_branch_settings_cache") || "{}");
              liveMode = localStorage.getItem("ty_live_mode") || "monitor";
              soundOn = localStorage.getItem("ty_sound_on") !== "false";
            } catch { /* noop */ }

            const key = `${data.brand}::${data.branch_id || `external:${data.store_id}`}`;
            const cfg = settings[key] || {};
            const tone = cfg.ringtone || "classic";
            const branchLabel = data.branch_name || `#${data.store_id}`;
            const priceLabel = fmtTL(data.total_price);

            // 1) Ses
            if (soundOn) playBeep(tone);
            // 2) Toast
            toast.success(`🍕 Yeni sipariş: ${data.brand} · ${branchLabel} · ${priceLabel}`, {
              duration: 10000,
              action: {
                label: "Görüntüle",
                onClick: () => { window.location.href = "/trendyol/live"; },
              },
            });
            // 3) Sistem bildirimi
            showNotification(
              `🍕 Yeni Sipariş · ${branchLabel}`,
              `${data.brand} · ${priceLabel}${data.order_code ? ` · Kod: ${data.order_code}` : ""}`,
              `ty-order-${pkgId}`,
            );

            // 4) Otomatik onay (liveMode=auto + auto_accept)
            if (liveMode === "auto" && cfg.auto_accept) {
              api.post("/trendyol/actions/accept", {
                brand: data.brand, package_id: pkgId,
                preparation_time_min: cfg.default_preparation_min || 20,
              }).then((res) => {
                if (!res.data?.already_accepted) {
                  toast.success(`⚡ Otomatik onaylandı: ${data.order_code || pkgId.slice(-4)} (${cfg.default_preparation_min || 20} dk)`);
                }
              }).catch((err) => {
                console.error("[global auto-accept] fail:", err);
                toast.error(`Otomatik onay hatası: ${err?.response?.data?.detail || err?.message}`);
              });
            }

            // 5) Otomatik yazdırma (liveMode=auto + auto_print)
            if (liveMode === "auto" && cfg.auto_print) {
              try {
                const r = await api.get(`/trendyol/order/${pkgId}`);
                const orderHtml = r.data?.order_html;  // backend'den HTML gelmiyorsa buildReceiptHTML çağır
                if (orderHtml) {
                  printOrderSilent(orderHtml);
                } else {
                  // Fallback: TrendyolLive sayfası açık değilse, sadece uyarı ver
                  toast.info("Yazdırma için Trendyol Canlı Sipariş sayfasını açın veya kiosk-print aktif kısayolu kullanın");
                }
              } catch (err) {
                console.error("[global auto-print] fail:", err);
              }
            }
          } catch (e) { console.warn("[global-live] parse fail:", e); }
        });

        es.onerror = () => {
          if (stopped) return;
          try { es.close(); } catch { /* noop */ }
          esRef.current = null;
          clearTimeout(reconnectRef.current);
          reconnectRef.current = setTimeout(connect, 5000);
        };
      } catch (e) { console.warn("[global-live] connect fail:", e); }
    };

    connect();

    return () => {
      stopped = true;
      clearTimeout(reconnectRef.current);
      if (esRef.current) { try { esRef.current.close(); } catch { /* noop */ } esRef.current = null; }
    };
  }, [user?.token]);
}
